import type { Fetchable } from '@morpho-org/blue-sdk'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'
import type {
  ColumnVisibilityState,
  PaginationState,
  RowData,
  TableFeatures
} from '@tanstack/react-table'

import { isDefined } from '@morpho-org/morpho-ts'
import { useMemo } from 'react'

import { getTableSkeletonRowCount } from '@/helpers/getTableSkeletonRowCount'

export interface UseSkeletonRowsProps<TFeatures extends TableFeatures, T extends RowData> {
  first: number
  totalCount: Fetchable<number>
  pagination: PaginationState
  columnsDefs: ColumnDefWithSkeleton<TFeatures, T>[]
  columnVisibility?: ColumnVisibilityState
}

/**
 * Hook that generates skeleton rows for table loading states.
 * @param params - Parameters object
 * @param params.first - Number of items per page
 * @param params.totalCount - Total item count from API
 * @param params.pagination - Pagination state with page index and size
 * @param params.columnsDefs - Column definitions with skeleton cells
 * @param params.columnVisibility - Column visibility state
 * @returns Array of skeleton rows matching visible columns
 */
export const useSkeletonRows = <TFeatures extends TableFeatures, T extends RowData>({
  first,
  totalCount,
  pagination,
  columnsDefs,
  columnVisibility
}: UseSkeletonRowsProps<TFeatures, T>) => {
  return useMemo(() => {
    const rowCount = getTableSkeletonRowCount(first, totalCount, pagination.pageIndex)
    return Array.from({ length: rowCount }, () =>
      columnsDefs
        .filter(def => {
          if (!isDefined(columnVisibility) || !isDefined(def?.id)) {
            return true
          }

          // By default, the column is visible
          if (!isDefined(columnVisibility[def.id])) {
            return true
          }

          return columnVisibility[def.id]
        })
        .map(def => def.skeletonCell)
    )
  }, [columnsDefs, first, pagination.pageIndex, totalCount, columnVisibility])
}
